import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from 'axios';
import Config from '../../config';
import { Card, Grid, Typography, CardContent, ButtonBase, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import ErrorIcon from '@mui/icons-material/Error';
import {EmbeddedOnboarding as SupplierOnboarding} from "../ccn/supplier_onboarding";
import { Alert } from '@mui/material';
import Reports from '../reports/reports';
import ContentLoader from '../../components/content_loader';
import moment from "moment";
import DownloadButton from "../../components/download_button";
import Auth from '../../auth';
import CloseIcon from '@mui/icons-material/Close';
import EventModal from '../messages/event_modal';

type FinancialStatus = 'ONBOARDED' | 'OFFERED' | 'ACCEPTED';

interface VwActivityHistoryRow {
    Attribute2: number;
}

// Window.KANMON_CONNECT is augmented canonically in pages/orders/v2/types.ts
// (single source of truth — see the note there). Re-declaring it here with a
// differently-shaped local interface makes the two `declare global` blocks fail
// to merge (TS2717), so this file just consumes the global types.ts provides.

interface FinancingBannerProps {
    companyName: string;
    financialStatus: FinancialStatus | null;
}

export function FinancingBanner({ companyName, financialStatus }: FinancingBannerProps): React.JSX.Element {
    const [loading, setLoading] = useState(true);
    const [availableToFinance, setAvailableToFinance] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const { data } = await axios.get<{ value: VwActivityHistoryRow[] }>(
                Config.api +
                    `/odata/company/VwActivityHistoryByDay?$select=Attribute2&$filter=(DocumentType eq 'Order') and (ActivityDateTime gt ${moment()
                        .add(-30, 'days')
                        .toISOString()})`
            );
            if (cancelled) return;
            const sum = data.value.map(r => r.Attribute2).reduce((a, b) => a + b, 0);
            setAvailableToFinance(sum);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const formatCurrency = (amount: number, currency?: string): string =>
        Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 0,
        }).format(amount);

    const getFinancing = async (): Promise<void> => {
        const { data } = await axios.post<{ Body: string }>(Config.api + '/api/v2/financing/connect');
        window.KANMON_CONNECT!.start({ connectToken: data.Body });
        window.KANMON_CONNECT!.show();
    };

    const getActionText = (status: FinancialStatus | null | undefined): string => {
        if (!status) return 'Get financing';
        if (status === 'ONBOARDED') return 'View application';
        if (status === 'OFFERED') return 'View offers';
        return 'Finish financing application';
    };

    const getSubText = (status: FinancialStatus | null | undefined): string => {
        if (status === 'OFFERED') return 'Finance orders from the Orders page';
        return formatCurrency(availableToFinance) + ' available for financing';
    };

    if (loading) {
        return <ContentLoader preserveAspectRatio="none" style={{ height: '6em', width: '100%' }} primaryColor="#e1e1e1" secondaryColor="#d8d8d8" />;
    }
    return (
        <Grid size={12}>
            <Card style={{ backgroundColor: '#EDF7FD' }}>
                <CardContent style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row' }}>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                        <Typography variant="h6">Need financing, {companyName}?</Typography>
                        <Typography>Get a small business loan from us today!</Typography>
                    </div>
                    <div>
                        <DownloadButton style={{ width: '18em' }} color="primary" variant="contained" onClick={getFinancing}>
                            {getActionText(financialStatus)}
                        </DownloadButton>
                        <Typography style={{ color: '#006BFF', fontWeight: 'bold', fontSize: '12px', marginTop: '0.25em' }}>
                            {getSubText(financialStatus)}
                        </Typography>
                    </div>
                </CardContent>
            </Card>
        </Grid>
    );
}

interface BannerEvent {
    Id: number;
    Summary: string;
}

interface CompanyProfile {
    Description?: string;
    Discoverable?: boolean;
    MerchFirstName?: string;
    MerchLastName?: string;
    MerchEmail?: string;
    Website?: string;
}

interface DashboardProps {
    setTitle: (title: string) => void;
    isRetailer: boolean;
    tier?: string;
    companyName: string;
    financingEnabled: boolean;
    financialStatus: FinancialStatus | null;
}

function Dashboard({
    setTitle,
    isRetailer,
    companyName,
    financingEnabled,
    financialStatus,
}: DashboardProps): React.JSX.Element {
    const [urgentMessages, setUrgentMessages] = useState<number | undefined>(undefined);
    const [profileIncomplete, setProfileIncomplete] = useState(false);
    const [bannerEvent, setBannerEvent] = useState<BannerEvent | undefined>(undefined);
    const [eventId, setEventId] = useState<number | null>(null);
    // Bumped on every banner-event click so <EventModal> re-mounts via the key
    // prop even when the user clicks the same banner twice in a row. Replaces
    // the original `await this.setState({eventId: null}); await this.setState(...)`
    // microtask trick.
    const [openSeq, setOpenSeq] = useState(0);

    // Per-call epoch token. Each invocation captures its epoch at the top; an
    // older in-flight call that resolves after a newer call started is detected
    // by `myEpoch !== loadEpoch.current` and exits before writing state. Mount
    // useEffect bumps the epoch in cleanup to cancel any in-flight call on
    // unmount.
    const loadEpoch = useRef(0);

    // Strict mount tracker, complementing loadEpoch. dismissEvent uses this to
    // skip its post-MarkEvents `load()` call entirely if the component
    // unmounted during the in-flight axios.get — saving 2–3 wasted requests
    // (loadEpoch alone would discard the writes but still issue the calls).
    const isMounted = useRef(true);
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        const myEpoch = ++loadEpoch.current;

        const urgent = (
            await axios.get<{ '@odata.count': number }>(
                Config.api +
                    "/odata/Company/VwCompanyPosts?$filter=RequireAck eq true and Acknowledged eq false and Sender ne Receiver&$count=true&$top=0"
            )
        ).data['@odata.count'];
        if (myEpoch !== loadEpoch.current) return;

        const events = (
            await axios.get<{ value: BannerEvent[] }>(
                Config.api +
                    "/odata/Company/VwActivityEvents?$filter=EventTypeId eq 56 and Viewed eq false and Category eq 'Portal' and EventLevel eq 'Alert'&$orderby=Id desc&$top=1"
            )
        ).data.value;
        if (myEpoch !== loadEpoch.current) return;

        let incomplete = false;
        if (!isRetailer) {
            const profile = (
                (await axios.get<{ value: CompanyProfile[] }>(Config.api + '/odata/Company/CompanyProfiles')).data.value || []
            )[0];
            if (myEpoch !== loadEpoch.current) return;
            incomplete = !!(
                profile &&
                !profile.Description &&
                profile.Discoverable !== false &&
                (profile.MerchFirstName || profile.MerchLastName || profile.MerchEmail || profile.Website)
            );
        }

        setUrgentMessages(urgent);
        setProfileIncomplete(incomplete);
        setBannerEvent(events[0]);
    }, [isRetailer]);

    useEffect(() => {
        setTitle('Dashboard');
        load();
        return () => {
            // Bumping the epoch invalidates whatever load() call is currently in flight.
            // We intentionally read the live ref at unmount, not a captured snapshot,
            // so the lint rule's "copy to local" advice doesn't apply here.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            loadEpoch.current++;
        };
    }, [setTitle, load]);

    const dismissEvent = useCallback(async () => {
        if (!bannerEvent) return;
        await axios.get(Config.api + '/odata/Company/Functions.MarkEvents?viewed=true&ids=' + bannerEvent.Id);
        if (!isMounted.current) return;
        await load();
    }, [bannerEvent, load]);

    const openBannerEvent = (id: number): void => {
        setEventId(id);
        setOpenSeq(s => s + 1);
    };

    return (
        <Grid container spacing={2}>
            {urgentMessages ? (
                <Grid size={12}>
                    <Link
                        style={{ color: 'white', fontSize: '16px' }}
                        to="/message-center?q.0=RequireAck%20eq%20true&q.1=Acknowledged%20eq%20false"
                    >
                        <Card id="cueMessage" style={{ backgroundColor: '#ee3224', color: 'white' }}>
                            <CardContent style={{ display: 'flex', alignItems: 'center' }}>
                                <ErrorIcon style={{ marginRight: '1rem' }} />
                                <div style={{ display: 'inline-block' }}>
                                    There {urgentMessages === 1 ? 'is' : 'are'} {urgentMessages === 1 ? 'one' : urgentMessages} message
                                    {urgentMessages === 1 ? '' : 's'} that require{urgentMessages === 1 ? 's' : ''} action. Click here to go to
                                    the message center.
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                </Grid>
            ) : (
                ''
            )}

            {bannerEvent ? (
                <Grid size={12} style={{ position: 'relative' }}>
                    <ButtonBase
                        style={{ color: 'white', fontSize: '16px', width: '100%', display: 'flex' }}
                        onClick={() => openBannerEvent(bannerEvent.Id)}
                    >
                        <Card id="cueBannerEvent" style={{ backgroundColor: '#ee3224', color: 'white', flex: 1 }}>
                            <CardContent style={{ display: 'flex', alignItems: 'center' }}>
                                <ErrorIcon style={{ marginRight: '1rem' }} />
                                <div style={{ display: 'inline-block', flex: 1, textAlign: 'left' }}>
                                    <span style={{ fontWeight: 'bold' }}>Most recent event:</span> {bannerEvent.Summary} Click to see details.
                                </div>
                            </CardContent>
                        </Card>
                    </ButtonBase>
                    <IconButton
                        style={{ color: 'white', padding: 0, position: 'absolute', right: '24px', top: '50%', transform: 'translateY(-50%)' }}
                        onClick={() => dismissEvent()}
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
            ) : (
                ''
            )}

            {profileIncomplete ? (
                <Grid size={12} style={{ display: 'flex' }}>
                    <Link style={{ flex: '1' }} to="/profile/account-information">
                        <Card style={{ flex: 1 }}>
                            <Alert severity="info">
                                <Typography style={{ textAlign: 'left' }}>
                                    Click here to complete your company profile. Unlock partnership opportunities, share your products with potential
                                    partners and allow partners to discover your company-specific details for easier order fulfillment through our
                                    Connected Commerce Network.
                                </Typography>
                            </Alert>
                        </Card>
                    </Link>
                </Grid>
            ) : (
                ''
            )}

            {financingEnabled && financialStatus !== 'ACCEPTED' && Auth.hasPermission('settings/manage') && (
                <FinancingBanner financialStatus={financialStatus} companyName={companyName} />
            )}

            {!isRetailer ? <SupplierOnboarding /> : ''}

            <Reports isRetailer={isRetailer} companyName={companyName} cols={{ lg: 12, md: 8, sm: 6, xs: 4, xxs: 2 }} />

            <EventModal key={openSeq} eventId={eventId} viewed={true} />
        </Grid>
    );
}

export default Dashboard;
